import { useCallback, useEffect, useState } from "react";
import {
  DELIVERY_HOME,
  DeliveryMethod,
  DeliveryZone,
  DELIVERY_PICKUP,
} from "../utils/deliveryQuote";

/**
 * Where the shopper wants their order, remembered across pages.
 *
 * The choice is made on a product page and read again in checkout, so it cannot live in component
 * state — and it deliberately does not live in Redux either: the cart is cleared on every
 * successful order and a municipality is not part of a cart. `localStorage` also survives the
 * reload, which is what makes the second product page already know the answer
 * (docs/ordering-flow/README.md §5.5).
 *
 * This is the cheap half of the highest-leverage change in the delivery work: a fee discovered on
 * the product page costs nothing, and the same fee discovered on the last screen of checkout is
 * the single largest cause of abandonment there is.
 */

const METHOD_KEY = "biai.delivery.method";
const MUNICIPALITY_KEY = "biai.delivery.municipality";

/** Safari in private mode throws on both read and write, and a shipping preference is not worth a
 *  crashed product page. Every access degrades to "no preference stored". */
const readStored = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignored on purpose: the choice still works for this page load.
  }
};

const isMethod = (value: string | null): value is DeliveryMethod =>
  value === DELIVERY_HOME || value === DELIVERY_PICKUP;

export type DeliverySelection = {
  method: DeliveryMethod;
  municipalityId: string;
  setMethod: (method: DeliveryMethod) => void;
  setMunicipalityId: (id: string) => void;
};

export const useDeliverySelection = (): DeliverySelection => {
  const [method, setMethodState] = useState<DeliveryMethod>(() => {
    const stored = readStored(METHOD_KEY);
    return isMethod(stored) ? stored : DELIVERY_HOME;
  });
  const [municipalityId, setMunicipalityState] = useState<string>(
    () => readStored(MUNICIPALITY_KEY) ?? "",
  );

  const setMethod = useCallback((next: DeliveryMethod) => {
    setMethodState(next);
    writeStored(METHOD_KEY, next);
  }, []);

  const setMunicipalityId = useCallback((next: string) => {
    setMunicipalityState(next);
    writeStored(MUNICIPALITY_KEY, next);
  }, []);

  return { method, municipalityId, setMethod, setMunicipalityId };
};

/**
 * The zone the shopper picked, or `null`.
 *
 * `null` covers three cases the caller must treat identically — nothing chosen, a municipality the
 * table no longer holds, and one that has been suspended — because all three mean the same thing:
 * there is no fee to show and `POST /orders` will refuse this delivery. A suspended zone resolving
 * to `null` is what stops a stale tab from quoting a municipality the shop stopped serving.
 */
export const findActiveZone = (
  zones: DeliveryZone[] | undefined,
  municipalityId: string,
): DeliveryZone | null =>
  zones?.find((zone) => zone.id === municipalityId && zone.is_active === true) ?? null;

/** Ordered for the selector, with suspended municipalities left out. */
export const selectableZones = (zones: DeliveryZone[] | undefined): DeliveryZone[] =>
  (zones ?? [])
    .filter((zone) => zone.is_active === true)
    .slice()
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

/**
 * The municipality to fall back on when the shopper has not chosen one (EB-554).
 *
 * The first zone by `display_order`, which is Yarumal — the shop's own municipality
 * (`Cl. 18 #20-71`) and the one most orders go to. Read from the data rather than hardcoded: the
 * ordering is already the answer to "which municipality matters most here", and a literal
 * `"Yarumal"` in the frontend would quietly become wrong the day the zone table is re-ordered or
 * that zone is suspended.
 */
export const defaultMunicipalityId = (zones: DeliveryZone[] | undefined): string =>
  selectableZones(zones)[0]?.id ?? "";

/**
 * Keeps the stored municipality pointing at something the shop actually serves.
 *
 * Two failures, one fix, because they end in the same state. A shopper who chose a municipality the
 * shop has since suspended keeps a dead id in `localStorage` forever; a shopper who has never
 * chosen at all has nothing stored. Both used to leave the select empty — an unlabelled sliver of a
 * control next to "Enviar a:", no fee, and nothing saying why — so both now resolve to the default
 * above, and the shopper sees a real municipality with a real shipping cost from the first product
 * page onward. It is written through to `localStorage` like any other choice, which is what carries
 * it into checkout.
 *
 * Runs once the zones have loaded, and only then — an empty list mid-fetch is not evidence.
 */
export const useReconcileMunicipality = (
  zones: DeliveryZone[] | undefined,
  municipalityId: string,
  setMunicipalityId: (id: string) => void,
) => {
  useEffect(() => {
    if (!zones || zones.length === 0) return;
    if (municipalityId && findActiveZone(zones, municipalityId)) return;

    setMunicipalityId(defaultMunicipalityId(zones));
  }, [zones, municipalityId, setMunicipalityId]);
};
